import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { fetchConvergence } from "@/lib/api";
import { DisclaimerBanner } from "@/components/Disclaimer";
import ConvergenceRow from "@/components/ConvergenceRow";
import PullToRefresh from "@/components/PullToRefresh";

export default function Home() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await fetchConvergence();
      setItems(data);
    } catch (e) {
      toast.error("Failed to load convergence feed");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <PullToRefresh onRefresh={load}>
    <div data-testid="home-page">
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between md:gap-4">
        <div>
          <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.24em] text-[#64748b]">
            Live Feed · Last 30 days
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-[#f8fafc] md:text-3xl">
            Convergence
          </h1>
          <p className="mt-1 max-w-xl text-sm text-[#94a3b8]">
            Ranked by weighted signal strength — independent voices carry more weight than corporate insiders.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid="refresh-btn"
            onClick={load}
            className="inline-flex items-center gap-2 border border-[#1e293b] bg-[#111827] px-3 py-2 text-xs font-medium text-[#94a3b8] transition-colors hover:border-[#334155] hover:text-[#f8fafc]"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Disclaimer banner */}
      <DisclaimerBanner />

      {/* Feed */}
      <div
        data-testid="convergence-feed"
        className="mt-4 border border-[#1e293b] bg-[#0b1120]"
      >
        {/* Column header — desktop only */}
        <div className="hidden grid-cols-[64px_1fr_auto_auto_auto] items-center gap-4 border-b border-[#1e293b] bg-[#0f172a] px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#475569] md:grid">
          <div>Rank</div>
          <div>Company</div>
          <div>Voices</div>
          <div>Signal</div>
          <div className="text-right">Share</div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-sm text-[#64748b]">
            Loading convergence feed…
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="ingest-empty-state"
            className="border-t border-[#1e293b] bg-[#0f172a] p-8 text-center"
          >
            <div className="mx-auto max-w-md">
              <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#f59e0b]">
                Ingestion in progress
              </div>
              <div className="mt-2 text-sm font-semibold text-[#f8fafc]">
                No verified mentions yet
              </div>
              <p className="mt-2 text-xs leading-relaxed text-[#94a3b8]">
                Signal Edge only publishes mentions that link back to a
                verifiable source (YouTube, podcast, earnings call, or article
                URL). We are backfilling the tracked-voice archive; verified
                mentions will appear here as the pipeline processes each source.
              </p>
            </div>
          </div>
        ) : (
          items.map((item, i) => (
            <ConvergenceRow key={item.ticker} item={item} index={i} />
          ))
        )}
      </div>
    </div>
    </PullToRefresh>
  );
}
